import api from "@/api/axios";

const DEFAULT_BRAND = {
  name: "QuantM Alpha",
  slug: "quantm-alpha",
  logo_url: null,
  wordmark_url: null,
  favicon_url: null,
  primary_color: "#1E40AF",
  secondary_color: "#64748B",
  accent_color: "#F59E0B",
  support_email: null,
  terms_url: null,
  privacy_url: null,
};

/**
 * Convert a hex color to oklch values for daisyUI v4 theme overrides.
 * Returns "L% C H" string (e.g. "43.20% 0.1900 278.00").
 */
function hexToOklch(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const toLinear = (c) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);

  // Linear RGB to LMS
  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const lc = Math.cbrt(l);
  const mc = Math.cbrt(m);
  const sc = Math.cbrt(s);

  // OKLab
  const L = 0.2104542553 * lc + 0.793617785 * mc - 0.0040720468 * sc;
  const a = 1.9779984951 * lc - 2.428592205 * mc + 0.4505937099 * sc;
  const bOk = 0.0259040371 * lc + 0.7827717662 * mc - 0.808675766 * sc;

  // OKLCH
  const C = Math.sqrt(a * a + bOk * bOk);
  let H = (Math.atan2(bOk, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return `${(L * 100).toFixed(2)}% ${C.toFixed(4)} ${H.toFixed(2)}`;
}

function getLightness(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const toLinear = (c) =>
    c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  const lr = toLinear(r);
  const lg = toLinear(g);
  const lb = toLinear(b);
  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;
  const lc = Math.cbrt(l);
  const mc = Math.cbrt(m);
  const sc = Math.cbrt(s);
  return 0.2104542553 * lc + 0.793617785 * mc - 0.0040720468 * sc;
}

export default {
  namespaced: true,
  state: {
    brand: { ...DEFAULT_BRAND },
    loaded: false,
    brandLoaded: false,
    error: null,
  },
  mutations: {
    setBrand(state, brand) {
      state.brand = { ...DEFAULT_BRAND, ...brand };
      state.loaded = true;
      state.brandLoaded = true;
      state.error = null;
    },
    setError(state, error) {
      state.error = error;
      state.loaded = true;
      state.brandLoaded = true;
    },
    clearBrand(state) {
      // Only nuke user-specific bits. The brand's visual identity (name, logo,
      // colors, support email, …) is tied to the hostname and stays the same
      // across logout/login — otherwise the post-logout login screen would
      // flash back to the default brand on a customer subdomain.
      state.brand = {
        ...state.brand,
        user_role: null,
      };
      state.loaded = true;
      state.brandLoaded = true;
    },
  },
  actions: {
    async fetchBrand({ commit, dispatch }) {
      try {
        const response = await api.get("/api/brand/current/");
        commit("setBrand", response.data);
        dispatch("applyBranding");
      } catch (error) {
        commit("setError", error.message || "Failed to fetch brand");
        dispatch("applyBranding");
      }
    },
    async fetchBrandCurrent({ dispatch }) {
      return dispatch("fetchBrand");
    },
    applyBranding({ state }) {
      const { brand } = state;
      const root = document.documentElement;

      // Set hex CSS custom properties for direct use in components
      root.style.setProperty("--brand-primary", brand.primary_color);
      root.style.setProperty("--brand-secondary", brand.secondary_color);
      root.style.setProperty("--brand-accent", brand.accent_color);

      // Override daisyUI theme colors so btn-primary, text-primary, etc. reflect brand
      root.style.setProperty("--p", hexToOklch(brand.primary_color));
      root.style.setProperty("--s", hexToOklch(brand.secondary_color));
      root.style.setProperty("--a", hexToOklch(brand.accent_color));

      // Set content colors (text on colored backgrounds) based on lightness
      const whiteContent = "100% 0 0";
      const darkContent = "0% 0 0";
      root.style.setProperty(
        "--pc",
        getLightness(brand.primary_color) < 0.6 ? whiteContent : darkContent,
      );
      root.style.setProperty(
        "--sc",
        getLightness(brand.secondary_color) < 0.6 ? whiteContent : darkContent,
      );
      root.style.setProperty(
        "--ac",
        getLightness(brand.accent_color) < 0.6 ? whiteContent : darkContent,
      );

      // Update Tailwind custom color properties
      root.style.setProperty("--primary-light", brand.primary_color);
      root.style.setProperty("--primary-dark", brand.primary_color);

      // Update favicon if brand provides one
      if (brand.favicon_url) {
        const favicon = document.querySelector('link[rel="icon"]');
        if (favicon) {
          favicon.href = brand.favicon_url;
        }
      }

      // Update document title
      document.title = brand.name;
    },
  },
  getters: {
    brand: (state) => state.brand,
    brandName: (state) => state.brand.name,
    brandSlug: (state) => state.brand.slug,
    logoUrl: (state) => state.brand.logo_url,
    wordmarkUrl: (state) => state.brand.wordmark_url,
    faviconUrl: (state) => state.brand.favicon_url,
    primaryColor: (state) => state.brand.primary_color,
    secondaryColor: (state) => state.brand.secondary_color,
    accentColor: (state) => state.brand.accent_color,
    supportEmail: (state) => state.brand.support_email,
    termsUrl: (state) => state.brand.terms_url,
    privacyUrl: (state) => state.brand.privacy_url,
    loaded: (state) => state.loaded,
    brandLoaded: (state) => state.brandLoaded,
    brandRole: (state) => state.brand?.user_role || null,
    allowedAdminPages: (state) => state.brand?.allowed_admin_pages || [],
    isDefaultBrand: (state) => Boolean(state.brand?.is_default),
  },
};
